import _extends from '@babel/runtime/helpers/extends';
import './Select.Menu.scss.js';
import React__default, { forwardRef, useRef, useCallback, useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import Checkbox from '../Checkbox/Checkbox.js';
import { classnames } from '../../../utils/classnames.js';
import Preloader from '../Preloader/Preloader.js';
import useForkRef from '../../../hooks/useForkRef.js';
import MenuItem from './Select.MenuItem.js';

var SelectMenu = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var options = _ref.options,
      selected = _ref.selected,
      multiselect = _ref.multiselect,
      selectId = _ref.selectId,
      menuVariantSize = _ref.menuVariantSize,
      variant = _ref.variant,
      isAsync = _ref.isAsync,
      infinityScrollProps = _ref.infinityScrollProps,
      query = _ref.query,
      preloader = _ref.preloader,
      onOptionClick = _ref.onOptionClick,
      onSearch = _ref.onSearch,
      onClose = _ref.onClose,
      onScroll = _ref.onScroll,
      inputRef = _ref.inputRef,
      readOnly = _ref.readOnly;
  var hasInfinityScroll = typeof onSearch === 'function';
  var optionRef = useRef(null);
  var makeLazyFetch = useCallback(function () {
    if (onSearch && isAsync) {
      return function () {
        return onSearch(query, isAsync);
      };
    }

    return function () {
      return undefined;
    };
  }, [onSearch, isAsync, query]);

  var handleKeyDown = function handleKeyDown(event) {
    var option = optionRef.current;

    if (event.code === 'Space') {
      event.preventDefault();

      if (option && multiselect) {
        onOptionClick(option);
      }
    }

    if (event.key === 'Tab') {
      event.preventDefault();

      if (event.shiftKey) {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      } else {
        onClose();
      }
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }

    if (event.code === 'Enter') {
      event.preventDefault();

      if (option) {
        onOptionClick(option);

        if (multiselect) {
          onClose();
        }
      }
    }

    var list = getList();

    if (!list) {
      return;
    }

    if (event.code === 'ArrowDown') {
      event.preventDefault();
      moveFocus(list, nextItem);
    }

    if (event.code === 'ArrowUp') {
      event.preventDefault();
      moveFocus(list, previousItem);
    }
  };

  var loader = /*#__PURE__*/React__default.createElement("div", {
    className: "rf-select-menu__preloader",
    "data-v-6e52f9c7": ""
  }, /*#__PURE__*/React__default.createElement(Preloader, {
    size: "m",
    "data-v-6e52f9c7": ""
  }));
  var selectedMap = useMemo(function () {
    return selected.reduce(function (acc, o) {
      acc[o.value] = true;
      return acc;
    }, {});
  }, [selected]);
  var firstIndex = options.findIndex(function (option) {
    return !option.disabled;
  });
  var listJSX = options.map(function (option, index) {
    var active = selectedMap[option.value] || false;

    var handleChange = function handleChange(event) {
      event.stopPropagation();
      onOptionClick(option);
    };

    var handleFocus = function handleFocus() {
      optionRef.current = option;
    };

    var label = option.node || option.label; // TODO: думаю это можно вынести в отдельный компонент (Highlighter)

    if (!option.node && query) {
      var indexStart = option.label.toLowerCase().indexOf(query.toLowerCase());

      if (indexStart >= 0) {
        var indexEnd = indexStart + query.length - 1;
        var left = '';
        var highlighted = '';
        var right = '';

        for (var i = 0; i < option.label.length; i++) {
          if (i < indexStart) {
            left += option.label[i];
            continue;
          }

          if (i >= indexStart && i <= indexEnd) {
            highlighted += option.label[i];
            continue;
          }

          right += option.label[i];
        }

        label = /*#__PURE__*/React__default.createElement(React__default.Fragment, null, left, /*#__PURE__*/React__default.createElement("span", {
          className: "rf-select-menu__highlight",
          "data-v-6e52f9c7": ""
        }, highlighted), right);
      }
    }

    return /*#__PURE__*/React__default.createElement(MenuItem, {
      key: option.value,
      autoFocus: !!readOnly && firstIndex === index,
      option: option,
      active: active,
      onFocus: handleFocus,
      "data-v-6e52f9c7": ""
    }, multiselect ? /*#__PURE__*/React__default.createElement(Checkbox, {
      style: {
        width: '100%',
        padding: '8px 16px',
        outline: 'none'
      },
      titleAtt: option.label,
      label: label,
      checked: active,
      onChange: handleChange,
      fullWidth: true,
      "data-v-6e52f9c7": ""
    }) : /*#__PURE__*/React__default.createElement("button", {
      tabIndex: -1,
      className: "rf-select-menu__item",
      title: option.node ? undefined : option.label,
      onClick: handleChange,
      "data-v-6e52f9c7": ""
    }, label));
  });
  var emptyJSX = /*#__PURE__*/React__default.createElement("div", {
    className: "rf-select-menu__empty",
    "data-v-6e52f9c7": ""
  }, "\u041D\u0435\u0442 \u0432\u0430\u0440\u0438\u0430\u043D\u0442\u043E\u0432");
  var ref = useRef(null);
  var forkedRef = useForkRef(ref, forwardedRef);

  var getList = function getList() {
    var _a, _b;

    var ul = ref.current;

    if (!hasInfinityScroll) {
      return ul;
    }

    return (_b = (_a = ul === null || ul === void 0 ? void 0 : ul.firstElementChild) === null || _a === void 0 ? void 0 : _a.firstElementChild) !== null && _b !== void 0 ? _b : null;
  };

  return /*#__PURE__*/React__default.createElement("ul", {
    ref: forkedRef,
    id: "Select-".concat(selectId, "-list-scroll"),
    "data-testid": "rf-select-list-scroll",
    className: classnames('rf-select-menu', "rf-select-menu--size--".concat(menuVariantSize), "rf-select-menu--variant--".concat(variant)),
    onKeyDown: handleKeyDown,
    onScroll: onScroll,
    "data-v-6e52f9c7": ""
  }, hasInfinityScroll ? /*#__PURE__*/React__default.createElement(InfiniteScroll, _extends({
    dataLength: 0,
    hasMore: false
  }, infinityScrollProps, {
    next: makeLazyFetch(),
    loader: loader,
    scrollableTarget: "Select-".concat(selectId, "-list-scroll"),
    "data-v-6e52f9c7": ""
  }), options.length ? listJSX : emptyJSX) : /*#__PURE__*/React__default.createElement(React__default.Fragment, null, preloader ? loader : options.length ? listJSX : emptyJSX));
});

function moveFocus(list, traversalFunction) {
  var currentFocus = document.activeElement;
  var wrappedOnce = false;
  var nextFocus = traversalFunction(list, currentFocus);

  while (nextFocus) {
    if (nextFocus === list.firstChild) {
      if (wrappedOnce) {
        return;
      }

      wrappedOnce = true;
    }

    var nextFocusDisabled = nextFocus.getAttribute('aria-disabled') === 'true';

    if (!nextFocus.hasAttribute('tabindex') || nextFocusDisabled) {
      nextFocus = traversalFunction(list, nextFocus);
    } else {
      nextFocus.focus();
      return;
    }
  }
}

function nextItem(list, item) {
  if (list === item) {
    return list.firstElementChild;
  }

  if (item && item.nextElementSibling) {
    return item.nextElementSibling;
  }

  return list.firstElementChild;
}

function previousItem(list, item) {
  if (list === item) {
    return list.lastElementChild;
  }

  if (item && item.previousElementSibling) {
    return item.previousElementSibling;
  }

  return list.lastElementChild;
}

export { SelectMenu as default };
